import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as AddIcon } from '../../../assets/svg/add-icon.svg';
import { ReactComponent as NoProjectsIcon } from '../../../assets/svg/no-projects.svg';
import { ReactComponent as BoxIcon } from '../../../assets/svg/box.svg';
import { ReactComponent as MoreOptionsIcon } from '../../../assets/svg/more-options.svg';
import { IconProduct, IconPage } from '../../../utils/icons';
import { capitalize } from 'lodash';
import { BrandProject } from '../../videoProduction/components/constants';
import axiosInstant from '../../../api';
import { getAllBrandProjects, getBrandProjects } from '../../../api/endpoints';
import { DotLottleLoader } from '../../../components/Loader';
import { useUser } from '../../../hooks/useUser';
import ProjectStatusChip from '../../videoProduction/components/ProjectStatusChip';
import {
  BRAND_PROJECT_STATUS_STYLES,
  getSpecialStatus,
  getBriefIconStatus,
  getTdcShippingIconStatus,
  getExpertShippingIconStatus,
  getPhaseColor,
} from '../../videoProduction/helper';
import StatusIcon from '../../videoProduction/components/StatusIcon';
import { ReactComponent as ClockIcon } from '../../../assets/svg/clock.svg';
import { ShippingDialog } from '../../videoProduction/components/ShippingDialog';
import { FilmingScheduling } from '../../../components/FilmingScheduling';
import AddProjectModal from '../../videoProduction/components/AddProjectModal';
import { ProjectApprovalModal } from '../../videoProduction/components/ProjectApprovalModal';
import { CancelProjectDialog } from '../../videoProduction/components/CancelProjectDialog';
import { CancelledProjectDialog } from '../../videoProduction/components/CancelledProjectDialog';
import {
  cancelBlockedReason,
  briefBlockedReason,
  restoreBlockedReason,
} from '../../videoProduction/components/projectStatusActions';
import { BriefDialog, BriefReviewDialog } from '../../../components/Brief';
import { downloadBriefPDF } from '../../../utils/briefPdfGenerator';
import {
  BRAND_PROJECT_BRIEF_STATUSES,
  BRAND_PROJECT_STATUSES,
  areExpertsApproved,
  canAccessStatus,
  getStatusAccessErrorMessage,
  isStatusClickable,
  getStatusAction,
  getStatusNavigationPath,
} from '../../videoProduction/helper';
import { isInternal, isBrand, isRMN, isExpert } from '../../../utils';
import { PriceBreakdownTooltip } from '../../../components/Pricing/PriceBreakdownTooltip';
import ExpertSelectionRecommendationDialog from '../../videoProduction/expertSelection/ExpertSelectionRecommendationDialog';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePortalPath } from '../../../hooks/usePortalPath'
import { useWorkspaceBrandId } from '../../../hooks/useWorkspaceBrandId';

interface ActiveProjectsProps {
  limit?: number;
  onCount?: (count: number) => void;
  // Full-row card (To-Do hidden): lay projects out two-up on wide screens,
  // wrapping to one column as it narrows.
  wide?: boolean;
}

const ActiveProjects: React.FC<ActiveProjectsProps> = ({
  limit = 20,
  onCount,
  wide = false,
}) => {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const workspaceBrandId = useWorkspaceBrandId();
  const { user, role } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<BrandProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedProject, setSelectedProject] = useState<BrandProject | null>(
    null,
  );
  const [showShippingDialog, setShowShippingDialog] = useState(false);
  const [showSchedulingDialog, setShowSchedulingDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBriefDialog, setShowBriefDialog] = useState(false);
  const [showBriefReviewDialog, setShowBriefReviewDialog] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCancelledDialog, setShowCancelledDialog] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDownloadingBrief, setIsDownloadingBrief] = useState(false);

  const fetchActiveProjects = async () => {
    try {
      setLoading(true);
      let response;

      // Staff in a workspace have no brand of their own — branch on the
      // resolved id, not on the viewer's membership.
      const brandId = workspaceBrandId || user?.brand?.[0]?.id;
      if (brandId) {
        response = await axiosInstant.get(
          `${getBrandProjects(brandId)}?status=active&limit=${limit}`,
        );
      } else {
        response = await axiosInstant.get(
          `${getAllBrandProjects()}?status=active&limit=${limit}`,
        );
      }

      if (response.data?.rows) {
        setProjects(response.data.rows);
        const c = response.data.count || 0;
        setTotalCount(c);
        onCount?.(c);
      } else if (response.data?.data) {
        setProjects(response.data.data);
        const c = response.data.count || 0;
        setTotalCount(c);
        onCount?.(c);
      }
    } catch (error) {
      console.error('Error fetching active projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchActiveProjects();
    }
  }, [limit, user]);

  const handleNewOrder = () => {
    navigate(p('/video-production/orders?create=new'));
  };

  const handleViewAll = () => {
    navigate(p('/video-production/projects?tab=active'));
  };

  const handleProjectClick = (projectId: number) => {
    navigate(p(`/video-production/projects/${projectId}?tab=details`));
  };


  const handleMoreOptions = (
    project: BrandProject,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    setSelectedProject(project);
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEditProject = () => {
    setShowEditModal(true);
    handleCloseMenu();
  };

  /**
   * Single door into the brief dialog. Submitting a brief writes
   * status = 'Brief Review' server-side with no status check, so opening it on a
   * Cancelled project silently un-cancels it — and the chip is not the only way
   * in: this menu item and the brief status icon reach the same dialog.
   */
  const openBriefDialogFor = (project: BrandProject | null) => {
    const blocked = briefBlockedReason(project);
    if (blocked) {
      toast.error(blocked);
      return;
    }
    setShowBriefDialog(true);
  };

  const handleEditBrief = () => {
    openBriefDialogFor(selectedProject);
    handleCloseMenu();
  };

  const handleDownloadBrief = async () => {
    if (!selectedProject || !selectedProject.briefs?.[0]) return;

    setIsDownloadingBrief(true);
    try {
      await downloadBriefPDF(selectedProject.briefs[0]);
    } catch (error) {
      console.error('Error downloading brief:', error);
    } finally {
      setIsDownloadingBrief(false);
      handleCloseMenu();
    }
  };

  const handleCancelProject = () => {
    setShowCancelDialog(true);
    handleCloseMenu();
  };

  const handleSelectExperts = () => {
    if (selectedProject) {
      navigate(
        p(`/video-production/projects/${selectedProject.id}?tab=expertSelection`),
      );
    }
    handleCloseMenu();
  };

  const handleStatusClick = (
    project: BrandProject,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    setSelectedProject(project);

    if (!canAccessStatus(project, project.status, role || '', user)) {
      toast.error(getStatusAccessErrorMessage(project.status));
      return;
    }

    const action = getStatusAction(project.status, role || '');

    if (action.showToast) {
      toast.success(action.showToast);
      return;
    }

    switch (action.type) {
      case 'approval':
        setShowApprovalModal(true);
        break;
      case 'cancelled':
        setShowCancelledDialog(true);
        break;
      case 'edit':
        handleEditProject();
        break;
      case 'scheduling':
        setShowSchedulingDialog(true);
        break;
      case 'expertSelection':
        setSearchParams({ expertSelection: project.id.toString() });
        break;
      case 'videoReview':
      case 'expertProposals':
      case 'briefDenied': {
        // getStatusNavigationPath is a pure helper returning absolute paths,
        // so the portal prefix is applied here rather than inside it.
        const path = getStatusNavigationPath(project, action);
        if (path) {
          navigate(p(path));
        }
        break;
      }
      case 'briefReview':
        setShowBriefReviewDialog(true);
        break;
      case 'briefDialog':
      default:
        setShowBriefDialog(true);
        break;
    }
  };

  const handleCloseModals = () => {
    setShowShippingDialog(false);
    setShowSchedulingDialog(false);
    setShowEditModal(false);
    setShowBriefDialog(false);
    setShowBriefReviewDialog(false);
    setShowApprovalModal(false);
    setShowCancelDialog(false);
    setShowCancelledDialog(false);
    setSelectedProject(null);

    setSearchParams((prev) => {
      prev.delete('expertSelection');
      return prev;
    });
  };

  const startCase = (text: string) =>
    text.replace(/\b\w+(?:'\w+)?\b/g, (word: string) => capitalize(word));

  const getStatusProp = (
    status: string,
    prop: 'color' | 'textColor' | 'icon',
  ) => {
    // Status values are display strings ("Pre-Production", "Video Review") —
    // resolve the style key by value, not by uppercasing the status.
    const statusKey = Object.keys(BRAND_PROJECT_STATUSES).find(
      (key) => BRAND_PROJECT_STATUSES[key] === status,
    ) as keyof typeof BRAND_PROJECT_STATUS_STYLES;
    return BRAND_PROJECT_STATUS_STYLES[statusKey]?.[prop];
  };


  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full min-h-0">
        <DotLottleLoader fill={false} />
      </Box>
    );
  }

  if (projects.length === 0) {
    return (
      <Box className="flex flex-col h-full min-h-0">
        <Box className="flex items-center justify-between mb-4 flex-shrink-0">
          <Typography
            sx={{
              color: 'var(--text)',
              fontFamily: 'CircularStd',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
            }}
          >
            Projects
          </Typography>
        </Box>

        <Box
          className="flex-1 flex flex-col items-center justify-center w-full"
          sx={{ gap: '21px' }}
        >
          <NoProjectsIcon />
          <Typography
            sx={{
              color: 'var(--Base-600, #727272)',
              fontFamily: '"CircularStd"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 450,
              lineHeight: 'normal',
              textAlign: 'center',
            }}
          >
            You have no active projects
          </Typography>
          <Button
            onClick={handleNewOrder}
            sx={{
              backgroundColor: 'var(--accent)',
              color: 'var(--Base-0, #FFF)',
              fontFamily: 'CircularStd',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 900,
              lineHeight: 'normal',
              textTransform: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              '&:hover': {
                backgroundColor: '#8A6D41',
              },
            }}
          >
            <AddIcon />
            {isBrand(role) || isRMN(user) ? 'Request A Quote' : 'New Order'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col h-full min-h-0">
      <Box className="flex items-center justify-between mb-4 flex-shrink-0 max-md:flex-wrap max-md:gap-2">
        <Typography
          sx={{
            color: 'var(--text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '15px', md: '18px' },
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          }}
        >
          Active Projects ({totalCount})
        </Typography>
        <Box className="flex items-center gap-4 max-md:gap-2">
          <Button
            onClick={handleViewAll}
            sx={{
              color: 'var(--accent-text)',
              fontFamily: 'CircularStd',
              fontSize: { xs: '12px', md: '14px' },
              fontWeight: 450,
              textTransform: 'none',
              padding: 0,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            View All
          </Button>
          <Button
            onClick={handleNewOrder}
            sx={{
              backgroundColor: 'var(--accent)',
              color: 'var(--Base-0, #FFF)',
              fontFamily: 'CircularStd',
              fontSize: { xs: '11px', md: '14px' },
              fontStyle: 'normal',
              fontWeight: 900,
              lineHeight: 'normal',
              textTransform: 'none',
              borderRadius: '12px',
              padding: { xs: '6px 12px', md: '8px 16px' },
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#8A6D41',
              },
            }}
          >
            + {isBrand(role) || isRMN(user) ? 'Request A Quote' : 'New Order'}
          </Button>
        </Box>
      </Box>

      <Box className="flex-1 min-h-0 overflow-y-auto">
        <Box className={wide ? "grid grid-cols-1 lg:grid-cols-2 gap-4" : "space-y-4"}>
          {projects.map((project) => (
            <Box
              key={project.id}
              className="rounded-[16px] border border-border-token bg-surface p-4 cursor-pointer hover:border-accent transition-colors max-md:p-3"
              onClick={() => handleProjectClick(project.id)}
            >
              <Box className="flex items-center justify-between gap-3 w-full max-md:gap-2">
                <Box className="flex gap-3 items-center flex-1 min-w-0 max-md:gap-2">
                  <Box className="flex w-[80px] h-[80px] items-center justify-center bg-white rounded-2xl overflow-hidden flex-shrink-0 max-md:w-[44px] max-md:h-[44px]">
                    {project?.product?.image ? (
                      <img
                        src={project.product.image}
                        alt="product icon"
                        className="w-full h-full object-cover rounded-xl"
                      />
                    ) : (
                      <IconProduct />
                    )}
                  </Box>

                  <Box className="flex flex-col items-start gap-1 flex-1 min-w-0">
                    <Typography
                      sx={{
                        color: 'var(--text)',
                        fontFamily: 'CircularStd',
                        fontSize: { xs: '13px', md: '14px' },
                        fontWeight: 700,
                        lineHeight: '132%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                      }}
                    >
                      {project?.title || 'Untitled Project'}
                    </Typography>

                    {project?.product?.title && (
                      <Typography
                        sx={{
                          color: 'var(--text-2)',
                          fontFamily: 'CircularStd',
                          fontSize: '12px',
                          fontWeight: 450,
                          lineHeight: '132%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}
                      >
                        {project?.product?.title}
                      </Typography>
                    )}

                    {project.content_type?.name && (
                      <Box
                        sx={{
                          display: 'inline-block',
                          backgroundColor: 'var(--surface-2)',
                          color: 'var(--text-2)',
                          fontFamily: 'CircularStd',
                          fontSize: '12px',
                          fontWeight: 450,
                          padding: '2px 8px',
                          borderRadius: '16px',
                          marginTop: 0.5,
                        }}
                      >
                        {startCase(
                          project.content_type?.name.replace(/_/g, ' '),
                        )}
                      </Box>
                    )}
                    {!isExpert(role) && project.pricing_total != null && (
                      <PriceBreakdownTooltip
                        breakdown={project.pricing_breakdown}
                        pricingSnapshottedAt={project.pricing_snapshotted_at}
                        pricingTotal={project.pricing_total}
                        project={project}
                      />
                    )}
                  </Box>
                </Box>

                <Box className="flex items-center gap-2 flex-shrink-0">
                  <Box
                    onClick={(e) => handleStatusClick(project, e)}
                    style={{
                      cursor: isStatusClickable(
                        project,
                        project.status,
                        role || '',
                        user,
                      )
                        ? 'pointer'
                        : 'not-allowed',
                      opacity: isStatusClickable(
                        project,
                        project.status,
                        role || '',
                        user,
                      )
                        ? 1
                        : 0.5,
                    }}
                  >
                    <ProjectStatusChip
                      background={
                        getStatusProp(project.status, 'color') || '#F2EBE2'
                      }
                      text={startCase(project.status || 'Active')}
                      textColor={
                        getStatusProp(project.status, 'textColor') || '#AB854A'
                      }
                      icon={
                        getStatusProp(project.status, 'icon') || (
                          <ClockIcon className="w-4 h-4" />
                        )
                      }
                    />
                  </Box>

                  <span
                    className="max-md:!hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {(() => {
                      const tdc = getTdcShippingIconStatus(project, role || '', user);
                      const expert = getExpertShippingIconStatus(project, role || '', user);
                      return (
                        <>
                          <StatusIcon
                            phase={tdc.phase}
                            needsAction={tdc.needsAction}
                            tooltip={tdc.tooltip}
                            icon={<BoxIcon style={{ color: getPhaseColor(tdc.phase) }} />}
                            label="TDC"
                            onClick={() => { setSelectedProject(project); setShowShippingDialog(true); }}
                          />
                          <StatusIcon
                            phase={expert.phase}
                            needsAction={expert.needsAction}
                            tooltip={expert.tooltip}
                            icon={<BoxIcon style={{ color: getPhaseColor(expert.phase) }} />}
                            label="EXPERT"
                            onClick={() => { setSelectedProject(project); setShowShippingDialog(true); }}
                          />
                        </>
                      );
                    })()}
                  </span>

                  <span
                    className="max-md:!hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {(() => {
                      const s = getBriefIconStatus(project, role || '', user);
                      return (
                        <StatusIcon
                          phase={s.phase}
                          needsAction={s.needsAction}
                          tooltip={s.tooltip}
                          icon={<IconPage size={20} stroke={getPhaseColor(s.phase)} />}
                          onClick={() => { setSelectedProject(project); openBriefDialogFor(project); }}
                        />
                      );
                    })()}
                  </span>

                  <Tooltip title="More options" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMoreOptions(project, e)}
                      className="max-md:!hidden"
                    >
                      <MoreOptionsIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditProject}>Edit Project</MenuItem>

        {/* 'finalized' was never a real status value (the enum is 'Final
            Delivered'), so this item was never actually disabled. briefBlockedReason
            covers both terminal statuses; aria-disabled keeps the item keyboard
            reachable and lets the tooltip explain itself. */}
        <Tooltip title={briefBlockedReason(selectedProject) || ''} placement="left">
          <MenuItem
            onClick={handleEditBrief}
            aria-disabled={!!briefBlockedReason(selectedProject)}
            sx={{ opacity: briefBlockedReason(selectedProject) ? 0.5 : 1 }}
          >
            {selectedProject?.briefs?.[0]?.id ? 'Edit Brief' : 'Create Brief'}
          </MenuItem>
        </Tooltip>

        {selectedProject?.briefs?.[0] &&
          selectedProject.briefs[0].status ===
            BRAND_PROJECT_BRIEF_STATUSES.APPROVED && (
            <MenuItem
              onClick={handleDownloadBrief}
              disabled={isDownloadingBrief}
            >
              {isDownloadingBrief ? 'Downloading...' : 'Download Brief'}
            </MenuItem>
          )}

        <MenuItem onClick={handleSelectExperts}>Select Experts</MenuItem>

        {/* aria-disabled, NOT disabled, and NOT wrapped in a span: ButtonBase kills
            pointer events on `.Mui-disabled` (hiding the tooltip), and MenuList walks
            nextElementSibling without descending, so a wrapper drops the item out of
            keyboard navigation. Direct, non-disabled child satisfies both. */}
        {isInternal(role) &&
          selectedProject?.status !== BRAND_PROJECT_STATUSES.CANCELLED && (
            <Tooltip
              title={cancelBlockedReason(selectedProject) || ''}
              placement="left"
            >
              <MenuItem
                aria-disabled={!!cancelBlockedReason(selectedProject)}
                onClick={
                  cancelBlockedReason(selectedProject)
                    ? () => {
                        toast.error(
                          cancelBlockedReason(selectedProject) as string,
                        );
                        handleCloseMenu();
                      }
                    : handleCancelProject
                }
                sx={{
                  opacity: cancelBlockedReason(selectedProject) ? 0.5 : 1,
                }}
              >
                Cancel Project
              </MenuItem>
            </Tooltip>
          )}

        {/* Cancelled projects offer Restore instead — same dialog as the chip. */}
        {isInternal(role) &&
          selectedProject?.status === BRAND_PROJECT_STATUSES.CANCELLED && (
            <Tooltip
              title={restoreBlockedReason(selectedProject) || ''}
              placement="left"
            >
              <MenuItem
                aria-disabled={!!restoreBlockedReason(selectedProject)}
                onClick={() => {
                  setShowCancelledDialog(true);
                  handleCloseMenu();
                }}
                sx={{
                  opacity: restoreBlockedReason(selectedProject) ? 0.5 : 1,
                }}
              >
                Restore Project
              </MenuItem>
            </Tooltip>
          )}
      </Menu>

      {showShippingDialog && selectedProject && (
        <ShippingDialog
          project={selectedProject}
          onClose={handleCloseModals}
          onComplete={() => {
            handleCloseModals();
            fetchActiveProjects();
          }}
        />
      )}

      {showSchedulingDialog && selectedProject && (
        <FilmingScheduling
          project={selectedProject}
          onClose={handleCloseModals}
          onComplete={() => {
            handleCloseModals();
            fetchActiveProjects();
          }}
        />
      )}

      {showEditModal && selectedProject && (
        <AddProjectModal
          setOpen={setShowEditModal}
          fetchProjects={fetchActiveProjects}
          project={selectedProject}
          isEdit
        />
      )}

      {showBriefDialog && selectedProject && (
        <BriefDialog
          project={selectedProject}
          onClose={handleCloseModals}
          onComplete={() => {
            handleCloseModals();
            fetchActiveProjects();
          }}
        />
      )}

      {showBriefReviewDialog && selectedProject && (
        <BriefReviewDialog
          project={selectedProject}
          onClose={() => {
            setShowBriefReviewDialog(false);
            setSelectedProject(null);
          }}
          onComplete={() => {
            setShowBriefReviewDialog(false);
            setSelectedProject(null);
            fetchActiveProjects();
          }}
        />
      )}

      {showApprovalModal && selectedProject && (
        <ProjectApprovalModal
          project={selectedProject}
          onClose={handleCloseModals}
          onComplete={() => {
            handleCloseModals();
            fetchActiveProjects();
          }}
        />
      )}

      {showCancelDialog && selectedProject && (
        <CancelProjectDialog
          project={selectedProject}
          onClose={handleCloseModals}
          onComplete={() => {
            handleCloseModals();
            fetchActiveProjects();
          }}
        />
      )}

      {showCancelledDialog && selectedProject && (
        <CancelledProjectDialog
          project={selectedProject}
          onClose={handleCloseModals}
          onRestored={() => {
            handleCloseModals();
            fetchActiveProjects();
          }}
        />
      )}

      {searchParams.get('expertSelection') && (
        <ExpertSelectionRecommendationDialog
          onConfirm={() => {
            handleCloseModals();
            fetchActiveProjects();
          }}
        />
      )}
    </Box>
  );
};

export default ActiveProjects;
